/** @format */

import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Select,
  TextInput,
  Table,
  Checkbox,
  ActionIcon,
  Pagination,
  Skeleton,
  Badge,
  Text,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { showNotification } from "@mantine/notifications";
import { useDebouncedValue } from "@mantine/hooks";
import { formatISO } from "date-fns";
import {
  Eye,
  Search,
  SortAscending,
  SortDescending,
  Check,
  X,
} from "tabler-icons-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { apiWrapWithErrorWithData } from "../../../utilities/apiHelpers";
import {
  getDocLockerList,
  getAgreementTypesApi,
  getAgreementPartyNames,
} from "../../../utilities/apis/agreements";
import { getBussinessUnit } from "../../../utilities/apis/serviceProvider";
import {
  loadingStates,
  formatDate,
  formatIndianNumber,
} from "../../../utilities/utilities";
import { getStatusColor, getStatusText } from "../../../utilities/enums";
import { selectUserData } from "../../../redux/selectors";
import { INDIA_STATES_UNIONS } from "../AgreementForm2";

const statusOptions = [
  { label: "FINALISED", value: "Signature" },
  { label: "RE-FINALIZED", value: "RE-SIGNATURE" },
  { label: "PENDING SIGNATURE", value: "SENT" },
  { label: "PARTIALLY COMPLETED", value: "PARTIALLYCOMPLETED" },
  { label: "CANCELLED", value: "CANCELLED" },
  { label: "DECLINED", value: "DECLINED" },
  { label: "SIGNED", value: "SIGNED" },
  { label: "FORCECLOSED", value: "FORCECLOSED" },
];

const estampOption = [
  { label: "Yes", value: "Yes" },
  { label: "No", value: "No" },
];

const MAX_FILES = 10;

const FileSelectionOverlay = ({ open, onClose, selectedFiles, onConfirm }) => {
  const currentUser = useSelector(selectUserData) || {};
  const navigate = useNavigate();

  // State for filters
  const [filters, setFilters] = useState({
    statusType: "",
    estamp: null,
    agreementType: null,
    location: null,
    startDate: null,
    endDate: null,
    partyName: "",
    businessunit: "",
    sortBy: "id",
    sortDirection: "desc",
  });

  // State for data
  const [listing, setListing] = useState({
    list: null,
    agreementsCount: null,
    loaded: false,
  });

  const [agreementTypeOptions, setAgreementTypeOptions] = useState([]);
  const [businessunitList, setBusinessunitList] = useState([]);
  const [transformedData, setTransformedData] = useState([]);
  const [parties, setParties] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(loadingStates.NO_ACTIVE_REQUEST);

  // Local selection state (temporary until confirmed)
  const [tempSelectedIds, setTempSelectedIds] = useState([]);

  // Initialize with already selected files
  useEffect(() => {
    if (open && selectedFiles) {
      setTempSelectedIds(selectedFiles.map((f) => f.id));
    }
  }, [open, selectedFiles]);

  // Fetch business units
  useEffect(() => {
    const fetchBusinessUnits = async () => {
      try {
        const { data } = await getBussinessUnit();
        if (data) {
          setBusinessunitList(data.result || []);
        }
      } catch (error) {
        console.error("Error fetching business units:", error);
      }
    };
    fetchBusinessUnits();
  }, []);

  // Transform business units
  useEffect(() => {
    if (businessunitList.length > 0) {
      setTransformedData(
        businessunitList.map((unit) => ({
          value: unit.id + "",
          label: unit.name,
        }))
      );
    }
  }, [businessunitList]);

  // Fetch agreement types
  useEffect(() => {
    const getAgreementTypes = async () => {
      const resp = await apiWrapWithErrorWithData(getAgreementTypesApi());
      if (resp?.success && resp?.typesOfAgreement) {
        const uniqueValues = Array.from(new Set([...resp.typesOfAgreement]));
        const temp = uniqueValues.map((item) => ({ label: item, value: item }));
        setAgreementTypeOptions(temp);
      }
    };
    getAgreementTypes();
  }, []);

  // Fetch party names for docs locker autocomplete
  useEffect(() => {
    if (!open) return;
    const fetchParties = async () => {
      const response = await apiWrapWithErrorWithData(
        getAgreementPartyNames({
          listType: "agreementsDocLockerList",
        }),
      );
      if (response?.success) {
        setParties(response.partyNames || []);
      }
    };
    fetchParties();
  }, [open]);

  // Fetch agreements list
  const fetchAgreementsList = async () => {
    let filterOptions = {};

    // Date filters
    if (filters.startDate && filters.endDate) {
      filterOptions["createdAt"] = {
        gte: formatISO(filters.startDate),
        lte: formatISO(filters.endDate),
      };
    } else if (filters.startDate || filters.endDate) {
      showNotification({
        title: "Agreement notification",
        message: "Please select both start and end date",
        color: "red",
      });
      return;
    }

    setLoading(loadingStates.LOADING);

    const payload = {
      page: page,
      sortByOptions: {
        [filters.sortBy]: filters.sortDirection,
      },
      pageSize,
    };

    // Build filter options
    if (filters.agreementType) {
      filterOptions["typeOfAgreement"] = {
        equals: filters.agreementType,
      };
    }
    if (filters.statusType) {
      filterOptions["status"] = {
        equals: filters.statusType,
      };
    }
    if (filters.estamp) {
      filterOptions["agreementType"] = {
        equals: filters.estamp === "Yes" ? "eStamp" : "normal",
      };
    }
    if (filters.businessunit) {
      filterOptions["mapBusinessunit"] = {
        equals: filters.businessunit,
      };
    }
    if (filters.location) {
      filterOptions["location"] = filters.location;
    }
    if (filters.partyName.length > 0) {
      filterOptions["OR"] = [
        { secondParty: { path: "$.name", string_contains: filters.partyName } },
        {
          secondParty: {
            path: "$.indi_name",
            string_contains: filters.partyName,
          },
        },
        {
          secondParty: {
            path: "$.brandName",
            string_contains: filters.partyName,
          },
        },
      ];
    }

    payload["filterOptions"] = filterOptions;

    const response = await apiWrapWithErrorWithData(
      getDocLockerList({ ...payload })
    );

    if (response?.success && response?.agreements) {
      setListing({
        list: response?.agreements,
        agreementsCount: response.agreementsCount,
        loaded: true,
      });
    }

    setLoading(loadingStates.NO_ACTIVE_REQUEST);
  };

  // Fetch on mount and filter changes
  useEffect(() => {
    if (open) {
      fetchAgreementsList();
    }
  }, [open, page, pageSize, filters.sortBy, filters.sortDirection]);

  // Handle checkbox toggle
  const handleCheckboxChange = (file, isChecked) => {
    setTempSelectedIds((prev) => {
      if (isChecked) {
        // Check if adding this file would exceed the limit
        if (prev.length >= MAX_FILES) {
          showNotification({
            title: "Selection limit reached",
            message: `You can only select up to ${MAX_FILES} documents at a time.`,
            color: "orange",
            autoClose: 3000
          });
          return prev;
        }
        return [...prev, file.id];
      } else {
        return prev.filter((id) => id !== file.id);
      }
    });
  };

  // Handle select all
  const handleSelectAll = (isChecked) => {
    if (isChecked && listing.list) {
      const allIds = listing.list.map((item) => item.id);
      setTempSelectedIds((prev) => {
        const newIds = allIds.filter((id) => !prev.includes(id));
        const totalAfterSelection = prev.length + newIds.length;
        
        // Check if selecting all would exceed the limit
        if (totalAfterSelection > MAX_FILES) {
          const availableSlots = MAX_FILES - prev.length;
          if (availableSlots > 0) {
            showNotification({
              title: "Selection limit reached",
              message: `You can only select ${availableSlots} more document${availableSlots !== 1 ? 's' : ''}. Maximum is ${MAX_FILES} documents.`,
              color: "orange",
              autoClose: 3000
            });
            // Select only what fits within the limit
            return [...prev, ...newIds.slice(0, availableSlots)];
          } else {
            showNotification({
              title: "Selection limit reached",
              message: `You have already selected ${MAX_FILES} documents. Please deselect some to add more.`,
              color: "orange",
              autoClose: 3000
            });
            return prev;
          }
        }
        
        return [...prev, ...newIds];
      });
    } else {
      // Deselect all on current page
      if (listing.list) {
        const pageIds = listing.list.map((item) => item.id);
        setTempSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
      }
    }
  };

  // Handle view document
  const onViewClick = (agreementId) => {
    window.open(
      `/app/agreements_manager/agreements/details/${agreementId}`,
      "_blank"
    );
  };

  // Handle confirm selection
  const handleConfirm = () => {
    if (tempSelectedIds.length === 0) {
      showNotification({
        title: "No files selected",
        message: "Please select at least one document",
        color: "orange",
      });
      return;
    }

    // Get full file objects for selected IDs
    const selectedFileObjects = listing.list?.filter((item) =>
      tempSelectedIds.includes(item.id)
    ) || [];

    // Also include previously selected files that might not be in current page
    const previouslySelected = selectedFiles.filter((file) =>
      tempSelectedIds.includes(file.id)
    );

    // Merge and deduplicate
    const allSelected = [...previouslySelected];
    selectedFileObjects.forEach((file) => {
      if (!allSelected.find((f) => f.id === file.id)) {
        allSelected.push(file);
      }
    });

    onConfirm(allSelected);
    onClose();
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setFilters({
      statusType: "",
      estamp: null,
      agreementType: null,
      location: null,
      startDate: null,
      endDate: null,
      partyName: "",
      businessunit: "",
      sortBy: "id",
      sortDirection: "desc",
    });
    setPage(1);
  };

  // Handle sort toggle
  const toggleSort = () => {
    setFilters({
      ...filters,
      sortDirection: filters.sortDirection === "asc" ? "desc" : "asc",
    });
  };

  const data = listing?.list;
  const allPageIdsSelected =
    data?.every((item) => tempSelectedIds.includes(item.id)) || false;

  return (
    <Modal
      opened={open}
      onClose={onClose}
      size="95%"
      title={
        <Text size="xl" weight={600}>
          Select Documents for Chat
        </Text>
      }
      styles={{
        modal: { height: "90vh" },
        body: { height: "calc(90vh - 80px)", display: "flex", flexDirection: "column" },
      }}
    >
      {/* Filters Section */}
      <div className="flex flex-col gap-3 mb-4">
        {/* Row 1 */}
        <div className="flex gap-2 flex-wrap items-center">
          <Select
            placeholder="Select status"
            value={filters.statusType}
            onChange={(val) => setFilters({ ...filters, statusType: val })}
            data={statusOptions}
            style={{ minWidth: "200px" }}
            clearable
          />
          <Select
            placeholder="E-stamp"
            value={filters.estamp}
            onChange={(val) => setFilters({ ...filters, estamp: val })}
            data={estampOption}
            style={{ minWidth: "150px" }}
            clearable
          />
          <Select
            placeholder="Agreement type"
            value={filters.agreementType}
            onChange={(val) => setFilters({ ...filters, agreementType: val })}
            data={agreementTypeOptions}
            style={{ minWidth: "200px" }}
            clearable
            searchable
          />
          <Select
            placeholder="Business unit"
            value={filters.businessunit}
            onChange={(val) => setFilters({ ...filters, businessunit: val })}
            data={transformedData}
            style={{ minWidth: "200px" }}
            clearable
            searchable
          />
        </div>

        {/* Row 2 */}
        <div className="flex gap-2 flex-wrap items-center">
          <Select
            placeholder="Location"
            value={filters.location}
            onChange={(val) => setFilters({ ...filters, location: val })}
            data={INDIA_STATES_UNIONS}
            style={{ minWidth: "200px" }}
            clearable
            searchable
          />
          <TextInput
            placeholder="Party name"
            value={filters.partyName}
            onChange={(e) =>
              setFilters({ ...filters, partyName: e.target.value })
            }
            style={{ minWidth: "200px" }}
          />
          <DatePicker
            placeholder="Start date"
            value={filters.startDate}
            onChange={(val) => setFilters({ ...filters, startDate: val })}
            style={{ minWidth: "150px" }}
            clearable
          />
          <DatePicker
            placeholder="End date"
            value={filters.endDate}
            onChange={(val) => setFilters({ ...filters, endDate: val })}
            style={{ minWidth: "150px" }}
            clearable
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 items-center justify-between">
          <div className="flex gap-2">
            <Button color="green" onClick={() => fetchAgreementsList()}>
              Search
            </Button>
            <Button color="red" onClick={handleClearFilters}>
              Clear
            </Button>
          </div>

          <div className="flex gap-2 items-center">
            <Select
              value={filters.sortBy}
              onChange={(val) => setFilters({ ...filters, sortBy: val })}
              data={[
                { label: "Created at", value: "createdAt" },
                { label: "End date", value: "dueDateOfContract" },
                { label: "Type", value: "typeOfAgreement" },
                { label: "Agreement value", value: "agreementValue" },
                { label: "AIN", value: "id" },
              ]}
              style={{ width: "180px" }}
            />
            <ActionIcon onClick={toggleSort}>
              {filters.sortDirection === "asc" ? (
                <SortAscending size={24} />
              ) : (
                <SortDescending size={24} />
              )}
            </ActionIcon>
          </div>
        </div>

        {/* Selection Summary */}
        {tempSelectedIds.length > 0 && (
          <Badge color="blue" size="lg">
            {tempSelectedIds.length} document{tempSelectedIds.length !== 1 ? "s" : ""} selected
          </Badge>
        )}
      </div>

      {/* Table Section - Scrollable */}
      <div style={{ flex: 1, overflowY: "auto", marginBottom: "16px" }}>
        {loading === loadingStates.LOADING && (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} height={40} radius="md" />
            ))}
          </div>
        )}

        {listing.loaded &&
          loading !== loadingStates.LOADING &&
          Boolean(data?.length) && (
            <Table striped highlightOnHover>
              <thead>
                <tr>
                  <th style={{ width: "50px" }}>
                    <Checkbox
                      checked={allPageIdsSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </th>
                  <th>AIN</th>
                  <th>Type</th>
                  <th>Business Unit</th>
                  <th>Status</th>
                  <th>Value (₹)</th>
                  <th>Second Party</th>
                  <th>Date</th>
                  <th style={{ width: "80px", textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data
                  .filter(
                    (v) =>
                      v.agreementData.json && v.agreementData.json.typeOfAgreement
                  )
                  .map((row) => {
                    const isSelected = tempSelectedIds.includes(row.id);
                    return (
                      <tr
                        key={row.id}
                        style={{
                          backgroundColor: isSelected
                            ? "rgba(70, 189, 225, 0.1)"
                            : undefined,
                        }}
                      >
                        <td>
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) =>
                              handleCheckboxChange(row, e.target.checked)
                            }
                          />
                        </td>
                        <td>{`agreement-${row.id}`}</td>
                        <td>{row.agreementData.json.typeOfAgreement}</td>
                        <td>
                          {row.agreementData?.json?.location ||
                            row.jobOrder ||
                            "-"}
                        </td>
                        <td>
                          <Badge color={getStatusColor(row.status)}>
                            {getStatusText(row.status)}
                          </Badge>
                        </td>
                        <td>{formatIndianNumber(row.agreementValue)}</td>
                        <td>
                          {row.agreementData.json.brandName}
                          <br />
                          <span className="font-light">
                            {`(${
                              row.agreementData?.json?.secondParty.name || "-"
                            })`}
                          </span>
                        </td>
                        <td>{formatDate(row.createdAt)}</td>
                        <td style={{ textAlign: "center" }}>
                          <ActionIcon
                            onClick={() => onViewClick(row.id)}
                            color="blue"
                            variant="light"
                          >
                            <Eye size={20} />
                          </ActionIcon>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </Table>
          )}

        {listing.loaded &&
          loading !== loadingStates.LOADING &&
          !data?.length && (
            <div
              className="flex justify-center items-center"
              style={{ minHeight: "200px" }}
            >
              <Text color="dimmed">No documents found</Text>
            </div>
          )}
      </div>

      {/* Pagination */}
      {listing.agreementsCount > 0 && (
        <div className="flex justify-center items-center gap-4 mb-4">
          <Pagination
            page={page}
            onChange={setPage}
            total={Math.ceil(listing.agreementsCount / pageSize)}
          />
          <Text size="sm">Page size:</Text>
          <Select
            style={{ width: "80px" }}
            data={["10", "20", "50"]}
            value={pageSize + ""}
            onChange={(val) => {
              setPageSize(parseInt(val));
              setPage(1);
            }}
          />
        </div>
      )}

      {/* Footer Actions */}
      <div className="flex justify-end gap-2 pt-3 border-t">
        <Button variant="subtle" color="gray" onClick={onClose} leftIcon={<X size={18} />}>
          Cancel
        </Button>
        <Button
          color="blue"
          onClick={handleConfirm}
          leftIcon={<Check size={18} />}
          disabled={tempSelectedIds.length === 0}
        >
          Confirm ({tempSelectedIds.length})
        </Button>
      </div>
    </Modal>
  );
};

export default FileSelectionOverlay;
